// Shared task-configuration display helpers (THUB-163).
//
// Both the Create Task wizard Review step (TaskCreator) and the task detail
// Configuration tab (TaskDetails) render a task's `configuration` object as a
// label/value grid. They previously hand-rolled their own filtering and DRIFTED:
// TaskDetails filtered out empty + workflow-specific fields, while TaskCreator's
// Review step dumped the whole object unfiltered, surfacing meaningless empty
// "Source Directory" / "Destination Directory" rows for hosted tasks where those
// remote-only fields don't apply. This helper is the single source of truth so
// the two surfaces can't drift again.
//
// Keys are camelCase on the frontend (wire convention); snake_case / SCREAMING
// variants are tolerated because some payloads arrive un-normalized.

import { HostingType, TransferMethod } from '../types/connection';
import { TaskConfiguration, TransferDirection } from '../types/task';

// --- Transport-aware wording (THUB-220) ------------------------------------------------
//
// The Create/Edit task forms describe the remote path and transfer direction to the user.
// These strings were hardcoded to "SFTP", so a remote Azure Blob or HTTP/API task showed
// SFTP-flavored copy ("partner's SFTP server", "Remote SFTP Path") even though the
// underlying transport is different. The field itself is transport-generic (an SFTP
// server directory, an Azure blob prefix within the container, or an HTTP resource path);
// only the label was misleading. These helpers pick the wording from the connection's
// transferMethod so the copy matches what actually runs.

interface RemotePathWording {
  /** Heading for the remote-path section, e.g. "Remote SFTP Path". */
  sectionHeading: string;
  /** Label for the inbound (pickup) path field. */
  sourceLabel: string;
  /** Label for the outbound (delivery) path field. */
  destinationLabel: string;
  /** Help text under the inbound path field. */
  sourceHelp: string;
  /** Help text under the outbound path field. */
  destinationHelp: string;
  /** Direction-option description when we pick up FROM the partner (inbound). */
  inboundDescription: string;
  /** Direction-option description when we push TO the partner (outbound). */
  outboundDescription: string;
}

const REMOTE_PATH_WORDING: Record<'sftp' | 'api' | 'azure_blob' | 'default', RemotePathWording> = {
  sftp: {
    sectionHeading: 'Remote SFTP Path',
    sourceLabel: 'Remote Source Path *',
    destinationLabel: 'Remote Destination Path *',
    sourceHelp: "The directory on the partner's SFTP server where files will be picked up from.",
    destinationHelp: "The directory on the partner's SFTP server where files will be delivered to.",
    inboundDescription: "We pick up files from partner's SFTP server",
    outboundDescription: "We push files to partner's SFTP server",
  },
  azure_blob: {
    sectionHeading: 'Azure Blob Path',
    sourceLabel: 'Blob Path Prefix *',
    destinationLabel: 'Blob Path Prefix *',
    sourceHelp: "The blob path prefix within the partner's Azure container where files will be picked up from.",
    destinationHelp: "The blob path prefix within the partner's Azure container where files will be delivered to.",
    inboundDescription: "We pick up files from the partner's Azure Blob container",
    outboundDescription: "We deliver files to the partner's Azure Blob container",
  },
  api: {
    sectionHeading: 'Remote HTTP Path',
    sourceLabel: 'Resource Path *',
    destinationLabel: 'Resource Path *',
    sourceHelp: "The resource path on the partner's HTTP endpoint where files will be picked up from.",
    destinationHelp: "The resource path on the partner's HTTP endpoint where files will be delivered to.",
    inboundDescription: 'Pull files from partner via HTTP API',
    outboundDescription: 'Push files to partner via HTTP API',
  },
  default: {
    sectionHeading: 'Remote Path',
    sourceLabel: 'Remote Source Path *',
    destinationLabel: 'Remote Destination Path *',
    sourceHelp: 'The path on the partner endpoint where files will be picked up from.',
    destinationHelp: 'The path on the partner endpoint where files will be delivered to.',
    inboundDescription: 'We pick up files from the partner endpoint',
    outboundDescription: 'We deliver files to the partner endpoint',
  },
};

/**
 * Transport-aware wording for the remote-path section + direction descriptions, keyed by
 * the connection's transferMethod. Falls back to neutral "partner endpoint" copy when the
 * method is unknown/unset. SFTP keeps its existing wording so nothing changes for it.
 */
export function remotePathWording(transferMethod?: TransferMethod): RemotePathWording {
  if (transferMethod === 'azure_blob') return REMOTE_PATH_WORDING.azure_blob;
  if (transferMethod === 'api') return REMOTE_PATH_WORDING.api;
  if (transferMethod === 'sftp') return REMOTE_PATH_WORDING.sftp;
  return REMOTE_PATH_WORDING.default;
}

// Human-readable labels for known configuration keys. Falls back to a
// de-camel/de-snake-cased version of the key when not listed.
const CONFIG_FIELD_LABELS: Record<string, string> = {
  sourceDirectory: 'Source Directory',
  destinationDirectory: 'Destination Directory',
  filePattern: 'File Pattern',
  preserveTimestamp: 'Preserve Timestamp',
  deleteAfterTransfer: 'Delete After Transfer',
  archiveProcessedFiles: 'Archive Processed Files',
  validateFileFormat: 'Validate File Format',
  notifyOnCompletion: 'Notify on Completion',
  notifyOnError: 'Notify on Error',
  postPickupAction: 'Post-Pickup Action',
  postPickupTargetPath: 'Post-Pickup Target Path',
  deliveryMode: 'Delivery Mode',
  deliverySchedule: 'Delivery Schedule',
};

// Remote-only configuration keys: these describe a path on the PARTNER's server
// (where TransferHub connects out). They are meaningless for hosted connections,
// where the partner connects to us and the path is auto-derived from the task
// name. Hidden at display time and pruned on save for non-remote tasks.
const REMOTE_ONLY_KEYS = new Set([
  'sourceDirectory',
  'source_directory',
  'destinationDirectory',
  'destination_directory',
]);

// Workflow-specific keys gated by hostingType + transferDirection. Mirrors the
// gating TaskDetails already applied to its own config render.
const WORKFLOW_SPECIFIC_FIELDS: Record<
  string,
  { hostingType: HostingType; transferDirection: TransferDirection }
> = {
  postPickupAction: { hostingType: 'remote', transferDirection: 'inbound' },
  post_pickup_action: { hostingType: 'remote', transferDirection: 'inbound' },
  postPickupTargetPath: { hostingType: 'remote', transferDirection: 'inbound' },
  post_pickup_target_path: { hostingType: 'remote', transferDirection: 'inbound' },
  deliveryMode: { hostingType: 'remote', transferDirection: 'outbound' },
  delivery_mode: { hostingType: 'remote', transferDirection: 'outbound' },
  deliverySchedule: { hostingType: 'remote', transferDirection: 'outbound' },
  delivery_schedule: { hostingType: 'remote', transferDirection: 'outbound' },
};

/** Human label for a configuration key. */
export function configFieldLabel(key: string): string {
  return (
    CONFIG_FIELD_LABELS[key] ||
    key
      .replace(/([A-Z])/g, ' $1')
      .replace(/[_]/g, ' ')
      .replace(/^./, str => str.toUpperCase())
      .trim()
  );
}

/** True when a remote-only path field should be hidden for this hosting type. */
export function isRemoteOnlyKeyHidden(
  key: string,
  hostingType?: HostingType
): boolean {
  return REMOTE_ONLY_KEYS.has(key) && hostingType !== 'remote';
}

/**
 * Return the configuration entries that are relevant to display for a task of
 * the given hosting type + transfer direction. Drops:
 *   - null/undefined/empty-string values
 *   - remote-only path keys when the connection is not remote
 *   - workflow-specific keys whose (hostingType, transferDirection) don't match
 *
 * When hostingType/transferDirection are undefined (e.g. not yet selected), the
 * workflow-specific and remote-only gating is skipped and only empty values are
 * dropped — callers that know the context should pass it.
 */
export function getRelevantConfigEntries(
  configuration: Record<string, unknown> | TaskConfiguration | undefined | null,
  hostingType?: HostingType,
  transferDirection?: TransferDirection
): Array<[string, unknown]> {
  if (!configuration) return [];
  return Object.entries(configuration).filter(([key, value]) => {
    // Hide empty values.
    if (value === null || value === undefined || value === '') return false;
    // Hide remote-only path fields for non-remote connections.
    if (hostingType !== undefined && isRemoteOnlyKeyHidden(key, hostingType)) {
      return false;
    }
    // Hide workflow-specific fields that don't apply to this task shape.
    const spec = WORKFLOW_SPECIFIC_FIELDS[key];
    if (spec) {
      if (hostingType !== undefined && spec.hostingType !== hostingType) {
        return false;
      }
      if (
        transferDirection !== undefined &&
        spec.transferDirection !== transferDirection
      ) {
        return false;
      }
    }
    return true;
  });
}

/**
 * Strip remote-only path keys from a configuration object for non-remote
 * connections, so hosted/HTTP tasks don't persist a meaningless empty
 * `sourceDirectory` / `destinationDirectory` (THUB-163 prune-on-save).
 * Returns a new object; does not mutate the input.
 */
export function pruneRemoteOnlyConfig<
  T extends TaskConfiguration | Record<string, unknown>
>(configuration: T, hostingType?: HostingType): T {
  // Only prune when we POSITIVELY know the connection is non-remote ('hosted').
  // If hostingType is undefined — e.g. the connection list hasn't resolved yet
  // on the edit path — preserve the config untouched rather than risk stripping
  // a remote task's source/destination path (data loss). 'remote' also preserved.
  if (hostingType !== 'hosted' || !configuration) return configuration;
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(configuration)) {
    if (REMOTE_ONLY_KEYS.has(key)) continue;
    cleaned[key] = value;
  }
  return cleaned as T;
}
